// react
import { useCallback, useState } from 'react';

// mui
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';

export interface AdminItem {
  name: string;
  status: 'pending' | 'active';
}

export interface AdminInviteFormPageProps {
  onBack: () => void;
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    email: string;
  }) => void | Promise<void>;
  administrators?: AdminItem[];
  isSubmitting?: boolean;
}

export const AdminInviteFormPage = ({
  onBack,
  onSubmit,
  administrators = [],
  isSubmitting = false,
}: AdminInviteFormPageProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);

  const isValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setTouched(true);
      if (!isValid) return;
      await onSubmit({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
      });
    },
    [firstName, lastName, email, isValid, onSubmit],
  );

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title='Add Organisation Admin'
            description='Invite a colleague (e.g. Paraplanner) to help manage referrals on your behalf.'
          />
        }
        contentSlot={
          <Box component='form' onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <Stack spacing={3}>
                <TextField
                  label='First name'
                  placeholder='John'
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  fullWidth
                  autoComplete='given-name'
                  error={touched && !firstName.trim()}
                  disabled={isSubmitting}
                />
                <TextField
                  label='Last name'
                  placeholder='Doe'
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  fullWidth
                  autoComplete='family-name'
                  error={touched && !lastName.trim()}
                  disabled={isSubmitting}
                />
                <Stack spacing={1}>
                  <TextField
                    label='Email'
                    placeholder='john.doe@example.com'
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    autoComplete='email'
                    error={touched && !email.trim()}
                    disabled={isSubmitting}
                  />
                  <Alert severity='info'>
                    They'll receive a magic link to join your organisation.
                  </Alert>
                </Stack>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant='h2'>Administrators</Typography>
                {administrators.map((admin, index) => (
                  <Stack
                    key={index}
                    direction='row'
                    spacing={2}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 16px',
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant='body1'>{admin.name}</Typography>
                    <Chip
                      label={admin.status === 'active' ? 'Active' : 'Pending'}
                      color={admin.status === 'active' ? 'success' : 'warning'}
                      size='medium'
                    />
                  </Stack>
                ))}
              </Stack>

              <Button
                type='submit'
                variant='contained'
                fullWidth
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? 'Sending…' : 'Send Invitation'}
              </Button>
            </Stack>
          </Box>
        }
      />
    </ModalPageTemplate>
  );
};

AdminInviteFormPage.displayName = 'AdminInviteFormPage';
export default AdminInviteFormPage;
